import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useOrganization } from '../../context/OrganizationContext'
import { useToast } from '../../context/ToastContext'
import { organizationsApi } from '../../services/organizationsApi'
import * as storeSubscriptionAdminApi from '../../services/storeSubscriptionAdminApi'
import * as storeMembersAdminApi from '../../services/storeMembersAdminApi'
import * as posAdminApi from '../../services/posAdminApi'
import { formatDate } from '../../utils/formatDate'
import { getCurrencyLabel } from '../../utils/currencies'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Modal } from '../../components/ui/Modal'
import { SkeletonDetailLayout, SkeletonTable } from '../../components/ui/Skeleton'
import * as supportAccess from '../../services/supportAccessService'
import { AddStoreModal } from './AddStoreModal'
import { OrgStatusBadge, isRestrictedOrgStatus, orgStatusDescription } from '../../utils/orgStatus'
import { formatStandardAddress } from '../../lib/formatStandardAddress'
import { PlatformStoreSetupProgress } from '../../components/onboarding/PlatformStoreSetupProgress'
import { setupProgressAdminApi } from '../../services/setupProgressAdminApi'

const featureLabels = {
  orders: 'Orders',
  inventory: 'Inventory',
  analytics: 'Analytics',
  marketing: 'Marketing',
  payments: 'Payments',
}

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  pending_mandate: 'Payment required',
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  expired: 'Expired',
  cancelled: 'Cancelled',
}

function subscriptionStatusClass(status: string): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    case 'pending_mandate':
    case 'past_due':
      return 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    case 'expired':
    case 'cancelled':
      return 'bg-destructive/15 text-destructive'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

type OrgStoreMemberRow = storeMembersAdminApi.StoreMemberAdminRow & {
  storeId: number
  storeName: string
}

export default function OrganizationDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    getOrganizationById,
    deleteOrganization,
    resendOwnerLoginDetails,
    sendStorefrontWelcome,
    clearError,
    mutationLoading,
    error,
  } = useOrganization()
  const [org, setOrg] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [stores, setStores] = useState([])
  const [storesLoading, setStoresLoading] = useState(false)
  const [storesError, setStoresError] = useState(null)
  const [posToggleBusy, setPosToggleBusy] = useState({})
  const [storefrontWelcomeModalOpen, setStorefrontWelcomeModalOpen] = useState(false)
  const [ownerResetModalOpen, setOwnerResetModalOpen] = useState(false)
  const [users, setUsers] = useState<OrgStoreMemberRow[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersError, setUsersError] = useState(null)
  const [userResetBusy, setUserResetBusy] = useState<Record<string, boolean>>({})
  const [userResetTarget, setUserResetTarget] = useState<OrgStoreMemberRow | null>(null)
  const [subscriptionByStoreId, setSubscriptionByStoreId] = useState<
    Record<number, storeSubscriptionAdminApi.StoreSubscriptionAdminSnapshot['subscription'] | null>
  >({})
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false)
  const [addStoreOpen, setAddStoreOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [supportModalOpen, setSupportModalOpen] = useState(false)
  const [supportAccessMode, setSupportAccessMode] = useState<'read_only' | 'full'>('read_only')
  const [supportReason, setSupportReason] = useState('')
  const [supportBusy, setSupportBusy] = useState(false)
  const [setupProgress, setSetupProgress] = useState(null)
  const [setupProgressLoading, setSetupProgressLoading] = useState(false)
  const [setupProgressError, setSetupProgressError] = useState(null)
  const { pushToast } = useToast()

  async function refreshOrganization() {
    const data = await getOrganizationById(id)
    setOrg(data)
    setLoadError(null)
    return data
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await getOrganizationById(id)
        if (!cancelled) {
          setOrg(data)
          setLoadError(null)
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Not found')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id, getOrganizationById])

  useEffect(() => {
    if (!org?.id) return
    let cancelled = false
    setStoresLoading(true)
    setStoresError(null)
    ;(async () => {
      try {
        const { items } = await organizationsApi.listStores(org.id)
        if (!cancelled) setStores(Array.isArray(items) ? items : [])
      } catch (e) {
        if (!cancelled) setStoresError(e?.message || 'Failed to load stores')
      } finally {
        if (!cancelled) setStoresLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [org?.id])

  useEffect(() => {
    if (!org?.id || !Array.isArray(stores) || stores.length === 0) {
      setSubscriptionByStoreId({})
      return
    }
    let cancelled = false
    setSubscriptionsLoading(true)
    ;(async () => {
      const entries = await Promise.all(
        stores.map(async (store) => {
          if (!store?.id) return [0, null] as const
          try {
            const snapshot = await storeSubscriptionAdminApi.getStoreSubscription(store.id)
            return [store.id, snapshot.subscription] as const
          } catch {
            return [store.id, null] as const
          }
        }),
      )
      if (cancelled) return
      setSubscriptionByStoreId(Object.fromEntries(entries))
    })().finally(() => {
      if (!cancelled) setSubscriptionsLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [org?.id, stores])

  useEffect(() => {
    if (!org?.id || !Array.isArray(stores) || stores.length === 0) {
      setUsers([])
      return
    }
    let cancelled = false
    setUsersLoading(true)
    setUsersError(null)
    ;(async () => {
      try {
        const rows: OrgStoreMemberRow[] = []
        for (const store of stores) {
          if (!store?.id) continue
          const { items } = await storeMembersAdminApi.listStoreMembers(store.id)
          for (const member of items ?? []) {
            rows.push({
              ...member,
              storeId: store.id,
              storeName: store.name ?? `Store ${store.id}`,
            })
          }
        }
        if (!cancelled) setUsers(rows)
      } catch (e) {
        if (!cancelled) setUsersError(e?.message || 'Failed to load store members')
      } finally {
        if (!cancelled) setUsersLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [org?.id, stores])

  useEffect(() => {
    if (!org?.id) return
    let cancelled = false
    setSetupProgressLoading(true)
    setSetupProgressError(null)
    ;(async () => {
      try {
        const data = await setupProgressAdminApi.getOrganisationProgress(org.id)
        if (!cancelled) setSetupProgress(data)
      } catch (e) {
        if (!cancelled) setSetupProgressError(e?.message || 'Failed to load setup progress')
      } finally {
        if (!cancelled) setSetupProgressLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [org?.id])

  async function refreshUsers() {
    if (!org?.id || !Array.isArray(stores) || stores.length === 0) {
      setUsers([])
      return
    }
    const rows: OrgStoreMemberRow[] = []
    for (const store of stores) {
      if (!store?.id) continue
      const { items } = await storeMembersAdminApi.listStoreMembers(store.id)
      for (const member of items ?? []) {
        rows.push({
          ...member,
          storeId: store.id,
          storeName: store.name ?? `Store ${store.id}`,
        })
      }
    }
    setUsers(rows)
  }

  async function handleDelete() {
    if (!org) return
    clearError()
    try {
      await deleteOrganization(org.id)
      pushToast({ message: 'Organization deleted', variant: 'success' })
      setDeleteModalOpen(false)
      navigate('/organizations', { replace: true })
    } catch {
      /* surfaced via context */
    }
  }

  async function handleTogglePos(store, nextEnabled) {
    if (!store?.id) return
    setPosToggleBusy((prev) => ({ ...prev, [store.id]: true }))
    setStoresError(null)
    try {
      await posAdminApi.patchStorePosSettings(store.id, nextEnabled)
      setStores((prev) =>
        Array.isArray(prev)
          ? prev.map((s) => (s.id === store.id ? { ...s, posEnabled: nextEnabled } : s))
          : prev,
      )
    } catch (e) {
      setStoresError(e?.message || 'Could not update POS setting')
    } finally {
      setPosToggleBusy((prev) => {
        const next = { ...prev }
        delete next[store.id]
        return next
      })
    }
  }

  async function handleOpenAsMerchant() {
    if (!org?.id) return
    setSupportBusy(true)
    clearError()
    try {
      const result = await supportAccess.mintOrganisationSupportAccess(org.id, {
        accessMode: supportAccessMode,
        reason: supportReason.trim() || undefined,
      })
      setSupportModalOpen(false)
      pushToast({ message: 'Support session started — store admin opened in a new tab', variant: 'success' })
      if (result.launchUrl) {
        window.open(result.launchUrl, '_blank', 'noopener,noreferrer')
      } else {
        pushToast({
          message: 'Session created but store admin URL is not configured (ORG_DASHBOARD_LOGIN_URL)',
          variant: 'info',
        })
      }
    } catch (e) {
      pushToast({
        message: e instanceof Error ? e.message : 'Failed to start support access',
        variant: 'error',
      })
    } finally {
      setSupportBusy(false)
    }
  }

  async function handleResendOwnerLogin() {
    if (!org) return
    clearError()
    setSuccessMessage('')
    try {
      await resendOwnerLoginDetails(org.id)
      await refreshOrganization()
      await refreshUsers()
      setSuccessMessage('Login details reset and emailed to shop owner successfully.')
    } catch {
      /* surfaced via context */
    } finally {
      setOwnerResetModalOpen(false)
    }
  }

  async function handleResetUserCredentials(member: OrgStoreMemberRow) {
    if (!member?.id || !member.storeId) return
    const busyKey = `${member.storeId}-${member.id}`
    clearError()
    setSuccessMessage('')
    setUserResetBusy((prev) => ({ ...prev, [busyKey]: true }))
    try {
      const data = await storeMembersAdminApi.resetStoreMemberCredentials(member.storeId, member.id)
      await refreshUsers()
      await refreshOrganization()
      setSuccessMessage(
        data?.emailSent
          ? `Login details reset and emailed to ${member.email}.`
          : `Password updated for ${member.email}, but the email could not be sent — check API email logs.`,
      )
    } catch (e) {
      setUsersError(e?.message || 'Could not reset login details')
    } finally {
      setUserResetBusy((prev) => {
        const next = { ...prev }
        delete next[busyKey]
        return next
      })
      setUserResetTarget(null)
    }
  }

  function roleLabel(role) {
    if (role === 'store_owner' || role === 'org_admin') return 'Shop owner'
    if (role === 'staff') return 'Staff'
    return role || '—'
  }

  function memberBusyKey(member: OrgStoreMemberRow | null) {
    if (!member?.storeId || !member?.id) return ''
    return `${member.storeId}-${member.id}`
  }

  const primaryStore =
    Array.isArray(stores) && stores.length > 0
      ? [...stores].sort((a, b) => Number(a.id) - Number(b.id))[0]
      : null
  const primarySubdomain = primaryStore?.subdomain?.trim() || ''
  const storefrontWelcomeSent = Boolean(org?.onboarding?.storefrontWelcomeEmailSentAt)
  const canSendStorefrontWelcome = primarySubdomain && !storefrontWelcomeSent

  function closeStorefrontWelcomeModal() {
    if (!mutationLoading) setStorefrontWelcomeModalOpen(false)
  }

  async function executeSendStorefrontWelcome() {
    if (!org) return
    clearError()
    setSuccessMessage('')
    try {
      const data = await sendStorefrontWelcome(org.id)
      await refreshOrganization()
      if (data?.alreadySent) {
        setSuccessMessage('Storefront welcome was already sent for this organization.')
      } else if (data?.emailSent) {
        setSuccessMessage('Storefront welcome email sent successfully.')
      } else {
        setSuccessMessage(
          'Storefront welcome email could not be sent (check API email logs). The owner password was updated — use Resend login details or try again.',
        )
      }
    } catch {
      /* surfaced via context */
    } finally {
      setStorefrontWelcomeModalOpen(false)
    }
  }

  if (loadError) {
    return (
      <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center">
        <p className="text-destructive">{loadError}</p>
        <Link to="/organizations" className="mt-4 inline-block">
          <Button type="button" variant="secondary">
            Back to list
          </Button>
        </Link>
      </div>
    )
  }

  if (!org) {
    return <SkeletonDetailLayout />
  }

  const enabledFeatures = Object.entries(org.features || {})
    .filter(([, v]) => v)
    .map(([k]) => featureLabels[k] || k)

  const ownerEmail = org.ownerAccess?.email || org.email

  return (
    <div className="space-y-6">
      <Modal
        open={ownerResetModalOpen}
        onClose={() => {
          if (!mutationLoading) setOwnerResetModalOpen(false)
        }}
        title="Reset shop owner login?"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              disabled={mutationLoading}
              onClick={() => setOwnerResetModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={mutationLoading} onClick={() => void handleResendOwnerLogin()}>
              {mutationLoading ? 'Resetting…' : 'Reset login'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground">
          Generate a new temporary password and email login details to{' '}
          <span className="font-medium">{ownerEmail}</span>? They will be required to change their
          password after signing in.
        </p>
      </Modal>

      <Modal
        open={Boolean(userResetTarget)}
        onClose={() => {
          const key = memberBusyKey(userResetTarget)
          if (!key || !userResetBusy[key]) setUserResetTarget(null)
        }}
        title="Reset user login?"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              disabled={Boolean(memberBusyKey(userResetTarget) && userResetBusy[memberBusyKey(userResetTarget)])}
              onClick={() => setUserResetTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={Boolean(memberBusyKey(userResetTarget) && userResetBusy[memberBusyKey(userResetTarget)])}
              onClick={() => userResetTarget && void handleResetUserCredentials(userResetTarget)}
            >
              {memberBusyKey(userResetTarget) && userResetBusy[memberBusyKey(userResetTarget)]
                ? 'Resetting…'
                : 'Reset login'}
            </Button>
          </>
        }
      >
        {userResetTarget ? (
          <p className="text-sm text-foreground">
            Generate a new temporary password and email login details to{' '}
            <span className="font-medium">{userResetTarget.email}</span>?
          </p>
        ) : null}
      </Modal>

      <Modal
        open={storefrontWelcomeModalOpen}
        onClose={closeStorefrontWelcomeModal}
        title="Send storefront welcome email?"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              disabled={mutationLoading}
              onClick={closeStorefrontWelcomeModal}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={mutationLoading}
              onClick={() => void executeSendStorefrontWelcome()}
            >
              {mutationLoading ? 'Sending…' : 'Send email'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-foreground">
          <p>
            Send the storefront welcome email to{' '}
            <span className="font-medium text-foreground">{ownerEmail}</span>?
          </p>
          <p className="text-muted-foreground">
            This tells them their customer store subdomain (
            <span className="font-mono text-foreground">{primarySubdomain}</span>) is ready, includes
            the store link when it is configured on the API, and sends new store admin login
            credentials. The owner{"'"}s password will be reset.
          </p>
        </div>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => {
          if (!mutationLoading) setDeleteModalOpen(false)
        }}
        title="Delete organization?"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              disabled={mutationLoading}
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={mutationLoading}
              onClick={() => void handleDelete()}
            >
              {mutationLoading ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-foreground">
          <p>
            Delete <span className="font-medium">{org?.name}</span>? This removes its stores, users,
            and products from the database.
          </p>
          <p className="text-muted-foreground">This action cannot be undone.</p>
        </div>
      </Modal>

      <Modal
        open={supportModalOpen}
        onClose={() => {
          if (!supportBusy) setSupportModalOpen(false)
        }}
        title="Open store admin (support)"
        size="md"
        footer={
          <>
            <Button type="button" variant="ghost" disabled={supportBusy} onClick={() => setSupportModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={supportBusy} onClick={() => void handleOpenAsMerchant()}>
              {supportBusy ? 'Starting…' : 'Open in new tab'}
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            Launch a time-limited support session in the merchant store admin. The session is logged and expires
            automatically (about one hour).
          </p>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Access level</span>
            <select
              className="min-h-10 rounded-xl border border-border bg-card px-3 py-2"
              value={supportAccessMode}
              onChange={(e) => setSupportAccessMode(e.target.value as 'read_only' | 'full')}
              disabled={supportBusy}
            >
              <option value="read_only">Read-only (recommended)</option>
              <option value="full">Full access</option>
            </select>
          </label>
          <p className="text-xs text-muted-foreground">
            {supportAccessMode === 'read_only'
              ? 'Read-only blocks writes in store admin and on the API — use for troubleshooting without changing merchant data.'
              : 'Full access allows merchant actions. Use only when you must make changes on their behalf.'}
          </p>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Reason (optional)</span>
            <textarea
              className="min-h-[4rem] rounded-xl border border-border bg-card px-3 py-2"
              value={supportReason}
              onChange={(e) => setSupportReason(e.target.value.slice(0, 500))}
              disabled={supportBusy}
              placeholder="e.g. Investigating checkout error for order #1042"
            />
          </label>
        </div>
      </Modal>

      {error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {successMessage ? (
        <div
          className={
            successMessage.includes('could not be sent')
              ? 'rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100'
              : 'rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200'
          }
        >
          {successMessage}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Organization</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-semibold text-foreground">{org.name}</h2>
            <OrgStatusBadge status={org.status} />
          </div>
          <p className="text-xs text-muted-foreground">Internal ref: {org.slug || '—'}</p>
          {isRestrictedOrgStatus(org.status) ? (
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{orgStatusDescription(org.status)}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/organizations">
            <Button type="button" variant="secondary">
              All organizations
            </Button>
          </Link>
          <Link to={`/organizations/${org.id}/edit`}>
            <Button type="button">Edit</Button>
          </Link>
          <Button
            type="button"
            variant="secondary"
            disabled={mutationLoading || supportBusy}
            onClick={() => setSupportModalOpen(true)}
          >
            Open as merchant
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={mutationLoading}
            onClick={() => setOwnerResetModalOpen(true)}
          >
            Reset owner login
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={mutationLoading || !canSendStorefrontWelcome}
            onClick={() => setStorefrontWelcomeModalOpen(true)}
            title={
              !primarySubdomain
                ? 'Set a store subdomain before sending the storefront welcome email.'
                : storefrontWelcomeSent
                  ? 'This welcome email has already been sent.'
                  : undefined
            }
          >
            {mutationLoading ? 'Sending…' : 'Send welcome email (storefront ready)'}
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={mutationLoading}
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      <PlatformStoreSetupProgress
        data={setupProgress}
        loading={setupProgressLoading}
        error={setupProgressError}
        organisationId={org.id}
        compactStores={stores.length > 1}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Profile" description="Primary tenant metadata." />
          <dl className="grid gap-4 sm:grid-cols-2">
            {[
              ['Email', org.email],
              ['Phone', org.phone],
              ['Country', org.country],
              ['Company default currency', getCurrencyLabel(org.currency)],
              ['Status', org.status],
              ['Owner login', org.ownerAccess?.mustChangePassword ? 'Pending first login' : 'Activated'],
              ['Owner email', org.ownerAccess?.email || org.email],
              [
                'Primary contact',
                [org.ownerAccess?.firstName, org.ownerAccess?.lastName].filter(Boolean).join(' ') ||
                  '—',
              ],
              ['Contact address', formatStandardAddress(org) || '—'],
              ['Created', formatDate(org.createdAt)],
              [
                'Onboarding email (admin ready)',
                org.onboarding?.initialEmailSentAt
                  ? formatDate(org.onboarding.initialEmailSentAt)
                  : 'Not recorded',
              ],
              [
                'Storefront welcome email',
                org.onboarding?.storefrontWelcomeEmailSentAt
                  ? formatDate(org.onboarding.storefrontWelcomeEmailSentAt)
                  : 'Not sent',
              ],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {label}
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card>
          <CardHeader
            title="Online shop"
            description="Their first online shop. Use the stores list below to change web address, currency, or contacts."
          />
          <div className="mb-4 flex h-2.5 overflow-hidden rounded-full ring-1 ring-border">
            <div
              className="flex-1"
              style={{
                backgroundColor:
                  org.storefront?.themePrimary ?? org.storefront?.themeColor ?? '#3b82f6',
              }}
            />
            <div
              className="flex-1"
              style={{
                backgroundColor: org.storefront?.themeSecondary ?? '#64748b',
              }}
            />
          </div>
          <p className="text-sm font-medium text-foreground">
            {org.storefront?.storeName || '—'}
          </p>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Shop contact
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {org.storefront?.contactEmail?.trim() || '—'}
                {org.storefront?.contactPhone?.trim()
                  ? ` · ${org.storefront.contactPhone}`
                  : ''}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                What this shop sells
              </dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {org.storefront?.sellingSummary?.trim() || '—'}
              </dd>
            </div>
          </dl>
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-muted/50 p-3">
            {(() => {
              const logo = org.storefront?.logoUrl
              if (!logo) return <p className="text-sm text-muted-foreground">No logo</p>
              const isImage = /^https?:\/\//i.test(logo) || logo.startsWith('data:')
              if (isImage) {
                return (
                  <img src={logo} alt="" className="max-h-16 w-auto object-contain" />
                )
              }
              return <span className="text-3xl leading-none">{logo}</span>
            })()}
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Store team"
          description="Owners and staff per store (store_memberships). Reset login sends a new temporary password by email."
        />
        {usersError ? <p className="text-sm text-destructive">{usersError}</p> : null}
        {usersLoading ? (
          <SkeletonTable rows={4} columns={7} />
        ) : users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No store admin members found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[42rem] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">S/N</th>
                  <th className="pb-2 pr-4 font-medium">Store</th>
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Email</th>
                  <th className="pb-2 pr-4 font-medium">Role</th>
                  <th className="pb-2 pr-4 font-medium">Login</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, rowIndex) => {
                  const busyKey = memberBusyKey(u)
                  return (
                  <tr key={`${u.storeId}-${u.id}`} className="border-b border-border/60 last:border-0">
                    <td className="py-3 pr-4 tabular-nums">{rowIndex + 1}</td>
                    <td className="py-3 pr-4 text-foreground">{u.storeName ?? '—'}</td>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="py-3 pr-4 text-foreground">{u.email ?? '—'}</td>
                    <td className="py-3 pr-4 text-foreground">{roleLabel(u.role)}</td>
                    <td className="py-3 pr-4 text-foreground">
                      {u.mustChangePassword ? 'Pending first login' : 'Activated'}
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-7 text-xs"
                        disabled={Boolean(busyKey && userResetBusy[busyKey])}
                        onClick={() => setUserResetTarget(u)}
                      >
                        {busyKey && userResetBusy[busyKey] ? '…' : 'Reset login'}
                      </Button>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AddStoreModal
        open={addStoreOpen}
        onClose={() => setAddStoreOpen(false)}
        organisationId={org.id}
        defaultCurrency={org.currency}
        onCreated={(created) => {
          setStores((prev) => {
            const list = Array.isArray(prev) ? prev : []
            const exists = list.some((s) => s.id === (created as { id?: number }).id)
            if (exists) {
              return list.map((s) =>
                s.id === (created as { id?: number }).id ? { ...s, ...(created as object) } : s,
              )
            }
            return [...list, created as (typeof list)[number]]
          })
          setSuccessMessage('Store created successfully.')
        }}
      />

      <Card>
        <CardHeader
          title="Stores"
          description="Each shop’s storefront currency is what customers pay in. The company default applies only when a shop does not set its own override."
          action={
            <Button type="button" size="sm" onClick={() => setAddStoreOpen(true)}>
              Add store
            </Button>
          }
        />
        {storesError ? (
          <p className="text-sm text-destructive">{storesError}</p>
        ) : storesLoading || subscriptionsLoading ? (
          <SkeletonTable rows={4} columns={9} />
        ) : stores.length === 0 ? (
          <p className="text-sm text-muted-foreground">No stores found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">S/N</th>
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Subdomain</th>
                  <th className="pb-2 pr-4 font-medium">Storefront currency</th>
                  <th className="pb-2 pr-4 font-medium">Company default</th>
                  <th className="pb-2 pr-4 font-medium">Plan</th>
                  <th className="pb-2 pr-4 font-medium">Subscription</th>
                  <th className="pb-2 pr-4 font-medium">In-store POS</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((s, rowIndex) => {
                  const sub = subscriptionByStoreId[s.id]
                  return (
                  <tr key={s.id} className="border-b border-border/60 last:border-0">
                    <td className="py-3 pr-4 tabular-nums">{rowIndex + 1}</td>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      {s.name ?? '—'}
                      {s.suspendedAt ? (
                        <span className="ml-2 inline-flex items-center rounded-full bg-destructive/15 px-2 py-0.5 align-middle text-[11px] font-medium text-destructive ring-1 ring-inset ring-destructive/30">
                          Suspended
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4 text-foreground">{s.subdomain ?? '—'}</td>
                    <td className="py-3 pr-4 text-foreground">
                      <div className="flex flex-col gap-1">
                        <span>{s.currency ? getCurrencyLabel(s.currency) : '—'}</span>
                        {s.currency &&
                        s.organisation?.currency &&
                        String(s.currency).toUpperCase() !==
                          String(s.organisation.currency).toUpperCase() ? (
                          <span className="w-fit rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                            Override
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">Follows company default</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-foreground">
                      {s.organisation?.currency
                        ? getCurrencyLabel(s.organisation.currency)
                        : '—'}
                    </td>
                    <td className="py-3 pr-4 text-foreground">
                      {sub?.planName ?? sub?.planCode ?? '—'}
                    </td>
                    <td className="py-3 pr-4">
                      {sub?.status ? (
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${subscriptionStatusClass(sub.status)}`}
                        >
                          {SUBSCRIPTION_STATUS_LABELS[sub.status] ?? sub.status}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            s.posEnabled
                              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {s.posEnabled ? 'On' : 'Off'}
                        </span>
                        <Button
                          type="button"
                          variant="secondary"
                          className="h-7 text-xs"
                          disabled={Boolean(posToggleBusy[s.id])}
                          onClick={() => void handleTogglePos(s, !s.posEnabled)}
                        >
                          {posToggleBusy[s.id] ? '…' : s.posEnabled ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Link to={`/organizations/${org.id}/stores/${s.id}`}>
                          <Button type="button" variant="secondary" className="text-xs">
                            View
                          </Button>
                        </Link>
                        <Link to={`/organizations/${org.id}/stores/${s.id}/pos-remittance`}>
                          <Button type="button" variant="secondary" className="text-xs">
                            Remittance
                          </Button>
                        </Link>
                        <Link to={`/organizations/${org.id}/stores/${s.id}/edit`}>
                          <Button type="button" variant="secondary" className="text-xs">
                            Edit
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Company profile"
          description="A short note about the company. What each shop sells is set on that shop."
        />
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              About the business
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
              {org.businessDescription?.trim() ? org.businessDescription : '—'}
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Enabled modules" description="Feature flags for this tenant." />
        <div className="flex flex-wrap gap-2">
          {enabledFeatures.length ? (
            enabledFeatures.map((label) => (
              <span
                key={label}
                className="rounded-full bg-primary/15 px-3 py-1 text-sm font-medium text-primary"
              >
                {label}
              </span>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No modules enabled.</p>
          )}
        </div>
      </Card>
    </div>
  )
}
